import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Download, FileText, Filter } from "react-feather";

import {
  listBatchesApi,
  salaryRegisterApi,
  pfReportApi,
  tdsReportApi,
  bankTransferApi,
} from "../../../../Api/payrollApi";
import { checkStatusCodeSuccess, rendorBackButton } from "../../../../Constant/common";
import { PAYROLL_STRINGS, PAYROLL_STATUS } from "../../../../Constant/HR/payrollConstants";
import BaseButton from "../../../../BaseComponents/BaseButton";
import BaseSelect from "../../../../BaseComponents/BaseSelect";
// import BaseInput from "../../../../BaseComponents/BaseInput";
import "./PayrollReports.css";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TABS = [
  { key: "salary_register", label: PAYROLL_STRINGS.REPORT_TAB_SALARY_REGISTER },
  { key: "pf_report", label: PAYROLL_STRINGS.REPORT_TAB_PF },
  { key: "tds_report", label: PAYROLL_STRINGS.REPORT_TAB_TDS },
  { key: "bank_transfer", label: PAYROLL_STRINGS.REPORT_TAB_BANK_TRANSFER },
];

/** Column definitions per report type */
const COLUMNS = {
  salary_register: [
    { key: "employeeName", label: "Employee Name" },
    { key: "employeeId", label: "Employee ID" },
    { key: "grossSalary", label: "Gross Salary", currency: true },
    { key: "totalDeductions", label: "Total Deductions", currency: true },
    { key: "netSalary", label: "Net Salary", currency: true },
  ],
  pf_report: [
    { key: "employeeName", label: "Employee Name" },
    { key: "uanNumber", label: "UAN Number" },
    { key: "basicSalary", label: "Basic Salary", currency: true },
    { key: "employeePf", label: "Employee PF", currency: true },
    { key: "employerPf", label: "Employer PF", currency: true },
  ],
  tds_report: [
    { key: "employeeName", label: "Employee Name" },
    { key: "panNumber", label: "PAN Number" },
    { key: "annualIncome", label: "Annual Income", currency: true },
    { key: "annualTax", label: "Annual Tax", currency: true },
    { key: "monthlyTds", label: "Monthly TDS", currency: true },
  ],
  bank_transfer: [
    { key: "employeeName", label: "Employee Name" },
    { key: "bankAccount", label: "Bank Account" },
    { key: "ifscCode", label: "IFSC Code" },
    { key: "netSalary", label: "Net Salary", currency: true },
  ],
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatINR = (val) => {
  if (val == null || isNaN(val)) return "—";
  return `₹${Number(val).toLocaleString("en-IN")}`;
};

const downloadCsv = (data, filename) => {
  if (!data || data.length === 0) return;
  const headers = Object.keys(data[0]);
  const rows = data.map((row) => headers.map((h) => `"${row[h] ?? ""}"`).join(","));
  const csv = [headers.join(","), ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const downloadPdf = (data, columns, reportTitle, batchLabel, isDraft) => {
  if (!data || data.length === 0) return;

  const watermarkBanner = isDraft
    ? `<div style="background:#f59e0b;color:#fff;text-align:center;padding:8px 0;font-size:13px;font-weight:700;letter-spacing:0.08em;">
        DRAFT — NOT FOR DISTRIBUTION
       </div>`
    : "";

  const headerRow = columns
    .map(
      (c) =>
        `<th style="padding:8px 12px;border:1px solid #e2e8f0;background:#f8fafc;font-size:12px;font-weight:700;text-align:left;">${c.label}</th>`,
    )
    .join("");

  const bodyRows = data
    .map(
      (row) =>
        `<tr>${columns
          .map(
            (c) =>
              `<td style="padding:7px 12px;border:1px solid #e2e8f0;font-size:12px;">${
                c.currency ? formatINR(row[c.key]) : (row[c.key] ?? "—")
              }</td>`,
          )
          .join("")}</tr>`,
    )
    .join("");

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${reportTitle}</title>
  <style>
    @media print {
      body { margin: 0; }
      @page { size: A4 landscape; margin: 16mm; }
    }
    body { font-family: Inter, Arial, sans-serif; color: #0f172a; }
    h2 { font-size: 16px; margin: 0 0 4px; }
    p  { font-size: 12px; color: #64748b; margin: 0 0 12px; }
    table { width: 100%; border-collapse: collapse; }
  </style>
</head>
<body>
  ${watermarkBanner}
  <div style="padding:16px 0 8px;">
    <h2>${reportTitle}</h2>
    <p>${batchLabel}${isDraft ? " · DRAFT" : ""}</p>
  </div>
  <table>
    <thead><tr>${headerRow}</tr></thead>
    <tbody>${bodyRows}</tbody>
  </table>
</body>
</html>`;

  const win = window.open("", "_blank");
  if (!win) {
    toast.error("Pop-up blocked. Please allow pop-ups to download PDF.");
    return;
  }
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => {
    win.print();
    win.close();
  }, 400);
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Single tab button */
const TabButton = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`payroll-tab-button ${active ? "active" : ""}`}
    aria-selected={active}
    role="tab"
  >
    {label}
  </button>
);

/** Empty state */
const EmptyState = ({ message }) => (
  <div className="payroll-reports-empty">
    <FileText size={36} aria-hidden="true" className="payroll-reports-empty-icon" />
    <p className="m-0">{message}</p>
  </div>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const PayrollReports = () => {
  const navigate = useNavigate();

  const [batches, setBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState("");
  const [activeTab, setActiveTab] = useState("salary_register");
  const [reportData, setReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  // const [departmentFilter, setDepartmentFilter] = useState("");

  // -------------------------------------------------------------------------
  // Fetch all batches on mount
  // -------------------------------------------------------------------------

  useEffect(() => {
    listBatchesApi({})
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          const data = Array.isArray(resp?.data)
            ? resp.data
            : Array.isArray(resp?.data?.data)
              ? resp.data.data
              : [];
          const sorted = [...data].sort((a, b) => {
            const aDate = new Date(a.year ?? 0, (a.month ?? 1) - 1);
            const bDate = new Date(b.year ?? 0, (b.month ?? 1) - 1);
            return bDate - aDate;
          });
          setBatches(sorted);
        } else {
          toast.error(resp?.message || PAYROLL_STRINGS.ERROR_GENERIC);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message || PAYROLL_STRINGS.ERROR_GENERIC);
      });
  }, []);

  // -------------------------------------------------------------------------
  // Fetch report data whenever batch, tab, or department filter changes
  // -------------------------------------------------------------------------

  const fetchReport = useCallback(() => {
    if (!selectedBatchId) {
      setReportData([]);
      return;
    }

    const params = {
      batchId: selectedBatchId,
      // department: departmentFilter || undefined,
    };

    const apiFn = {
      salary_register: salaryRegisterApi,
      pf_report: pfReportApi,
      tds_report: tdsReportApi,
      bank_transfer: bankTransferApi,
    }[activeTab];

    if (!apiFn) return;

    setIsLoading(true);
    apiFn(params)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          const data = Array.isArray(resp?.data) ? resp.data : [];
          setReportData(data);
        } else {
          toast.error(resp?.message || PAYROLL_STRINGS.ERROR_GENERIC);
          setReportData([]);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message || PAYROLL_STRINGS.ERROR_GENERIC);
        setReportData([]);
      })
      .finally(() => setIsLoading(false));
  }, [
    selectedBatchId,
    activeTab,
    //  departmentFilter
  ]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // -------------------------------------------------------------------------
  // Derived values
  // -------------------------------------------------------------------------

  const selectedBatch = batches.find((b) => String(b.id) === String(selectedBatchId)) ?? null;
  const isDraft = selectedBatch?.status === PAYROLL_STATUS.DRAFT;
  const batchLabel = selectedBatch
    ? selectedBatch.payrollMonth || `${selectedBatch.month}/${selectedBatch.year}`
    : "";

  const columns = COLUMNS[activeTab] ?? [];
  const previewRows = reportData.slice(0, 10);
  const activeTabObj = TABS.find((t) => t.key === activeTab);
  const reportTitle = activeTabObj?.label ?? "Report";

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleTabChange = (key) => {
    setActiveTab(key);
    setReportData([]);
  };

  const handleCsvDownload = () => {
    if (!reportData.length) {
      toast.info("No data to download.");
      return;
    }
    const filename = `${reportTitle.replace(/\s+/g, "_")}_${batchLabel}.csv`;
    downloadCsv(reportData, filename);
  };

  const handlePdfDownload = () => {
    if (!reportData.length) {
      toast.info("No data to download.");
      return;
    }
    setIsDownloading(true);
    try {
      downloadPdf(reportData, columns, reportTitle, batchLabel, isDraft);
    } finally {
      // Small delay so the loader is visible
      setTimeout(() => setIsDownloading(false), 600);
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="animate-fade-in d-flex flex-column gap-3">
      {/* Page header */}
      <div className="animate-slide-down d-flex align-items-center justify-content-between flex-wrap gap-3">
        <div>
          <h1 className="page-header-title">{PAYROLL_STRINGS.REPORTS_TITLE}</h1>
          <p className="page-header-subtitle">{PAYROLL_STRINGS.REPORTS_SUBTITLE}</p>
        </div>
        {rendorBackButton(() => navigate("/payrollTemplates"))}
      </div>

      {/* Filter bar */}
      <div className="payroll-reports-filter-bar glass rounded-3 animate-slide-up">
        <div className="payroll-reports-filter-icon">
          <Filter size={15} aria-hidden="true" />
        </div>

        {/* Batch selector */}
        <div className="payroll-reports-filter-group" style={{ maxWidth: "50%" }}>
          <BaseSelect
            name="batch-select"
            label="Payroll Batch"
            value={selectedBatchId}
            handleChange={(name, value) => {
              setSelectedBatchId(value);
              setReportData([]);
            }}
            options={batches.map((b) => ({
              value: b.id,
              label: `${b.payrollMonth || `${b.month}/${b.year}`} · ${b.status}`,
            }))}
            placeholder="— Select a batch —"
          />
        </div>

        {/* Department filter */}
        {/* <div className="payroll-reports-filter-group">
          <BaseInput
            name="dept-filter"
            label="Department"
            type="text"
            value={departmentFilter}
            handleChange={(e) => setDepartmentFilter(e.target.value)}
            placeholder="Filter by department…"
          />
        </div> */}
      </div>

      {/* Main report card */}
      <div className="payroll-reports-container glass rounded-3 animate-slide-up">
        {/* Tab bar */}
        <div role="tablist" aria-label="Report type" className="payroll-reports-tabbar">
          {TABS.map((tab) => (
            <TabButton
              key={tab.key}
              label={tab.label}
              active={activeTab === tab.key}
              onClick={() => handleTabChange(tab.key)}
            />
          ))}
        </div>

        {/* Tab content */}
        <div className="payroll-reports-content">
          {/* Action buttons */}
          <div className="payroll-reports-actions">
            <BaseButton
              color="secondary"
              onClick={handleCsvDownload}
              disabled={!reportData.length || isLoading}
              aria-label="Download CSV"
            >
              <Download size={14} aria-hidden="true" />
              Download CSV
            </BaseButton>

            <BaseButton
              color="primary"
              onClick={handlePdfDownload}
              loader={isDownloading}
              disabled={!reportData.length || isLoading}
              aria-label={isDraft ? "Download PDF (DRAFT watermark)" : "Download PDF"}
            >
              <Download size={14} aria-hidden="true" />
              {isDraft ? "Download PDF (DRAFT)" : "Download PDF"}
            </BaseButton>
          </div>

          {/* Loading state */}
          {isLoading ? (
            <div className="payroll-reports-loading">{PAYROLL_STRINGS.LOADING_GENERATING}</div>
          ) : !selectedBatchId ? (
            <EmptyState message={PAYROLL_STRINGS.EMPTY_REPORTS} />
          ) : previewRows.length === 0 ? (
            <EmptyState message="No data found for the selected batch and filters." />
          ) : (
            /* Preview table */
            <div className="payroll-reports-table-wrapper">
              <table className="payroll-reports-table" aria-label={`${reportTitle} preview`}>
                <thead>
                  <tr>
                    {/* DRAFT watermark badge in header when batch is Draft */}
                    {isDraft && (
                      <th colSpan={columns.length} className="payroll-reports-table-draft-warning">
                        <span className="payroll-reports-table-draft-text">
                          ⚠ DRAFT — NOT FOR DISTRIBUTION
                        </span>
                      </th>
                    )}
                  </tr>
                  <tr>
                    {columns.map((col) => (
                      <th key={col.key} scope="col">
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, idx) => (
                    <tr key={idx}>
                      {columns.map((col) => (
                        <td key={col.key} className={col.currency ? "currency" : ""}>
                          {col.currency ? formatINR(row[col.key]) : (row[col.key] ?? "—")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Row count note */}
              {reportData.length > 10 && (
                <p className="payroll-reports-row-count">
                  Showing 10 of {reportData.length} rows. Download CSV or PDF for the full report.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PayrollReports;
